import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, 
  Calendar, 
  Trophy, 
  Users, 
  TrendingUp, 
  Activity,
  Plus,
  ArrowRight,
  BarChart3,
  PieChart,
  Clock,
  Database,
  FileSpreadsheet,
  Shield,
  Swords,
  CheckCircle,
  XCircle,
  Loader
} from 'lucide-react';

import { getApiBaseUrl } from '../../utils/apiUrl';
const API_URL = getApiBaseUrl();

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pokemon: {},
    counters: {},
    events: {},
    imports: {}
  });
  const [loading, setLoading] = useState(true);

  const getAuthToken = () => {
    return localStorage.getItem('adminToken') || '';
  };

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/admin-stats/overview`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setStats(data.data);
          } else {
            console.error('Failed to load stats:', data.message || data.error);
            // Set empty stats on error
            setStats({
              pokemon: {},
              counters: {},
              events: {},
              imports: {}
            });
          }
        } else {
          // Handle non-200 responses
          const errorData = await response.json().catch(() => ({}));
          console.error('Error loading stats:', errorData.message || errorData.error);
          setStats({
            pokemon: {},
            counters: {},
            events: {},
            imports: {}
          });
        }
      } catch (error) {
        console.error('Error loading stats:', error);
        // Set empty stats on network error
        setStats({
          pokemon: {},
          counters: {},
          events: {},
          imports: {}
        });
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const handleQuickAction = (path) => {
    navigate(path);
  };

  const statCards = [
    {
      title: 'โปเกมอนทั้งหมด',
      value: stats.pokemon?.total || 0,
      icon: Zap,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-600 dark:text-green-400',
      change: stats.pokemon?.with_shiny ? `${Math.round((stats.pokemon.with_shiny / (stats.pokemon.total || 1)) * 100)}% มี Shiny` : '',
      changeType: 'positive',
      path: '/admin/pokemon'
    },
    {
      title: 'Raid Counters',
      value: stats.counters?.raid || 0,
      icon: Shield,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-600 dark:text-blue-400',
      change: '',
      changeType: 'positive',
      path: '/admin/pokemon'
    },
    {
      title: 'Max Battle Counters',
      value: stats.counters?.maxBattle || 0,
      icon: Swords,
      color: 'from-red-500 to-rose-600',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      textColor: 'text-red-600 dark:text-red-400',
      change: '',
      changeType: 'positive',
      path: '/admin/pokemon'
    },
    {
      title: 'Defenders',
      value: stats.counters?.defenders || 0,
      icon: Shield,
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      textColor: 'text-orange-600 dark:text-orange-400',
      change: '',
      changeType: 'positive',
      path: '/admin/pokemon'
    },
    {
      title: 'การนำเข้า',
      value: stats.imports?.total_imports || 0,
      icon: Database,
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
      textColor: 'text-indigo-600 dark:text-indigo-400',
      change: stats.imports?.successful ? `${stats.imports.successful} สำเร็จ` : '',
      changeType: 'positive',
      path: '/admin/pokemon/import'
    }
  ];

  const quickActions = [
    {
      title: 'เพิ่มโปเกมอนใหม่',
      description: 'เพิ่มโปเกมอนเข้าสู่ระบบ',
      icon: Plus,
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      hoverColor: 'hover:bg-green-100 dark:hover:bg-green-900/30',
      path: '/admin/pokemon'
    },
    {
      title: 'นำเข้าข้อมูล CSV',
      description: 'อัพโหลดและนำเข้าข้อมูลจากไฟล์ CSV',
      icon: FileSpreadsheet,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      hoverColor: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
      path: '/admin/pokemon/import'
    },
    {
      title: 'จัดการลีก',
      description: 'จัดการลีกและข้อมูลการแข่งขัน',
      icon: Trophy,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      hoverColor: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
      path: '/admin/leagues'
    }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'ยังไม่มีการนำเข้า';
    const date = new Date(dateString);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'สำเร็จ';
      case 'failed':
        return 'ล้มเหลว';
      case 'processing':
        return 'กำลังประมวลผล';
      default:
        return 'รอดำเนินการ';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">ยินดีต้อนรับสู่ Pokedex Admin</h1>
            <p className="text-blue-100 text-lg">
              จัดการข้อมูลโปเกมอน กิจกรรม และลีกได้อย่างง่ายดาย
            </p>
          </div>
          <div className="hidden md:block">
            <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center">
              <Zap className="w-16 h-16 text-white" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => stat.path && handleQuickAction(stat.path)}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                <stat.icon className={`w-6 h-6 ${stat.textColor}`} />
              </div>
              {stat.change && (
                <div className="text-right">
                  <div className={`text-sm font-medium ${
                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </div>
                </div>
              )}
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {stat.value.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {stat.title}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
      >
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
          การดำเนินการด่วน
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <motion.button
              key={action.title}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleQuickAction(action.path)}
              className={`p-6 rounded-xl ${action.bgColor} ${action.hoverColor} transition-all duration-200 text-left group cursor-pointer`}
            >
              <div className="flex items-center justify-between mb-3">
                <action.icon className={`w-6 h-6 ${action.color}`} />
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                {action.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {action.description}
              </p>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Recent Imports */}
      {stats.imports?.recent && stats.imports.recent.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              ประวัติการนำเข้าล่าสุด
            </h2>
            <button 
              onClick={() => handleQuickAction('/admin/pokemon/import')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
            >
              ดูทั้งหมด
            </button>
          </div>
          <div className="space-y-4">
            {stats.imports.recent.slice(0, 5).map((importItem, index) => (
              <motion.div
                key={importItem.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex-shrink-0">
                  {getStatusIcon(importItem.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 dark:text-white font-medium truncate">
                    {importItem.file_name}
                  </p>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {getStatusText(importItem.status)}
                    </span>
                    {importItem.imported_rows && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {importItem.imported_rows}/{importItem.total_rows} รายการ
                      </span>
                    )}
                    <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatDate(importItem.created_at)}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Pokemon Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            สรุปโปเกมอน
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">โปเกมอนปกติ</span>
              <span className="font-semibold text-gray-900 dark:text-white">{stats.pokemon?.normal || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Mega Forms</span>
              <span className="font-semibold text-gray-900 dark:text-white">{stats.pokemon?.mega_forms || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Gigantamax Forms</span>
              <span className="font-semibold text-gray-900 dark:text-white">{stats.pokemon?.gigantamax_forms || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Legendary</span>
              <span className="font-semibold text-gray-900 dark:text-white">{stats.pokemon?.legendary || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Mythical</span>
              <span className="font-semibold text-gray-900 dark:text-white">{stats.pokemon?.mythical || 0}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            สถิติการนำเข้า
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">สำเร็จ</span>
              <span className="font-semibold text-green-600">{stats.imports?.successful || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">ล้มเหลว</span>
              <span className="font-semibold text-red-600">{stats.imports?.failed || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">กำลังประมวลผล</span>
              <span className="font-semibold text-blue-600">{stats.imports?.processing || 0}</span>
            </div>
            {stats.imports?.last_import && (
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">นำเข้าล่าสุด</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(stats.imports.last_import)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
